import axios from "axios";
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom/client";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "../../styles/AIReporting.css";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; 
const STATUS = {
  OPEN: "open",
  LOCKED: "locked",
  SIGNED_OFF: "signed_off",
};
const STATUS_LABELS = {
  open: "Open",
  locked: "Locked",
  signed_off: "Signed Off",
};
const STATUS_COLORS = {
  open: { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
  locked: { bg: "#fff3e0", color: "#e65100", border: "#ffcc80" },
  signed_off: { bg: "#e3f2fd", color: "#0d47a1", border: "#90caf9" },
};

// ======================================================
// REPORT GENERATING LOADER COMPONENT
// ======================================================
const ReportGeneratingLoader = () => {
  const [dots, setDots] = useState("");
  const [step, setStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const steps = [
    { icon: "🔍", text: "Analyzing DICOM Images..." },
    { icon: "🧠", text: "AI model scan processing..." },
    { icon: "📋", text: "Findings extract..." },
    { icon: "✍️", text: "Report draft..." },
    { icon: "🔬", text: "Medical terminology verifying..." },
    { icon: "✅", text: "Final report preparation..." },
  ];

  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400);
    const stepInterval = setInterval(() => {
      setStep((prev) => (prev + 1) % steps.length);
    }, 3000);
    const timerInterval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => {
      clearInterval(dotsInterval);
      clearInterval(stepInterval);
      clearInterval(timerInterval);
    };
  }, []);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  return (
    <div className="ai-report">
      <div className="ai-report1">
        <div className="ai-report2" />
        <div className="ai-report3">
          <div className="ai-report4">🧠</div>
          <div className="ai-report5" />
          <div className="ai-report6" />
        </div>
        <div className="ai-report7">
          AI REPORT GENERATING
          <span className="ai-report8">{dots}</span>
        </div>
        <div className="ai-report9">Please wait — this process takes some time</div>
        <div className="ai-report10">
          <div className="ai-report11" />
        </div>
        <div className="ai-report12" key={step}>
          <span className="ai-report13">{steps[step].icon}</span>
          <span className="ai-report14">{steps[step].text}</span>
        </div>

        {/* Step dots */}
        <div className="ai-report15">
          {steps.map((_, i) => (
            <div
              className="ai-report16"
              key={i}
              style={{
                width: i === step ? "22px" : "8px",
                height: "8px",
                background: i === step ? "#2563eb" : "rgba(255,255,255,0.15)",
              }}
            />
          ))}
        </div>

        {/* Timer */}
        <div className="ai-report17">
          ⏱️ Generating since{" "}
          <span className="ai-report18">{formatTime(elapsed)}</span>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// AI REPORT POPUP COMPONENT (WITH STATUS TRACKING)
// ======================================================
const AIReportPopup = ({ report, patient, onClose }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [statusMsg, setStatusMsg] = useState("Ready");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [reportStatus, setReportStatus] = useState(STATUS.OPEN);
  const reportRef = useRef(null);
  const getStudyId = (p) => {
    if (p?.study_id) return p.study_id;
    if (p?.studyId) return p.studyId;
    if (p?.StudyInstanceUID) return p.StudyInstanceUID;
    if (p?.studies?.length > 0)
      return p.studies[0]?.study_id || p.studies[0]?.studyId || p.studies[0]?.StudyInstanceUID || "N/A";
    return "N/A";
  };

  const getStudyDescription = (p) => {
    if (p?.study_description) return p.study_description;
    if (p?.studyDescription) return p.studyDescription;
    if (p?.studies?.length > 0)
      return p.studies[0]?.study_description || p.studies[0]?.studyDescription || "N/A";
    return "N/A";
  };

  const resolvedPatient = patient?.patient || patient?.data || patient || {};
  const patientId =
    resolvedPatient?.patient_id ||
    resolvedPatient?.patientId ||
    resolvedPatient?.id ||
    resolvedPatient?.PatientID ||
    "N/A";

  const patientName =
    resolvedPatient?.patient_name ||
    resolvedPatient?.patientName ||
    resolvedPatient?.name ||
    resolvedPatient?.PatientName ||
    "N/A";

  const studyId = getStudyId(resolvedPatient);
  const studyDescription = getStudyDescription(resolvedPatient);

  // ======================================================
  // LOAD EXISTING REPORT STATUS FROM BACKEND
  // ======================================================
  useEffect(() => {
    if (studyId && studyId !== "N/A") {
      fetch(`${API_BASE_URL}/api/study-status/${studyId}/`)
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          if (data?.report_status) {
            setReportStatus(data.report_status);
          }
        })
        .catch(() => {
          console.log("Status fetch failed, using OPEN");
        });
    }
  }, [studyId]);

  // ======================================================
  // AI REPORT JSON PARSE
  // ======================================================
  let parsedReport = {};
  try {
    parsedReport = JSON.parse(report?.findings || "{}");
  } catch (err) {
    console.error("JSON PARSE ERROR:", err);
  }

  // ======================================================
  // EDITABLE SECTIONS STATE
  // ======================================================
  const [sections, setSections] = useState([
    { title: "Technique",               key: "protocol",     value: parsedReport.protocol     || "" },
    { title: "Findings",                key: "observations", value: parsedReport.observations || "" },
    { title: "Impression / Conclusion", key: "impression",   value: parsedReport.impression   || "" },
    { title: "Recommendation",          key: "advice",       value: parsedReport.advice       || "" },
  ]);

  const updateSection = (index, newValue) => {
    setSections((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], value: newValue };
      return updated;
    });
  };

  // ======================================================
  // UPDATE STUDY STATUS IN BACKEND
  // ======================================================
  const updateStudyStatus = async (newStatus) => {
    if (studyId === "N/A") return true;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/study-status/${studyId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ report_status: newStatus }),
      });
      return res.ok;
    } catch {
      return false;
    }
  };

  // ======================================================
  // SAVE REPORT (Lock it)
  // ======================================================
  const handleSaveReport = async () => {
    if (reportStatus === STATUS.SIGNED_OFF) {
      setStatusMsg("⛔ Signed-off reports cannot be saved.");
      return;
    }

    setIsSaving(true);
    setStatusMsg("💾 Saving report...");

    const reportToSave = {
      patientId,
      patientName,
      studyId,
      studyDescription,
      reportData: sections.reduce((acc, sec) => ({ ...acc, [sec.key]: sec.value }), {}),
      timestamp: new Date().toISOString(),
      reportType: "ai_report",
      report_status: STATUS.LOCKED,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/save-report/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reportToSave),
      });

      if (response.ok) {
        await updateStudyStatus(STATUS.LOCKED);
        setReportStatus(STATUS.LOCKED);
        setSaveSuccess(true);
        setStatusMsg("✅ Report saved and locked.");
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error("Failed to save report");
      }
    } catch (error) {
      console.error("Error saving report:", error);
      setStatusMsg("❌ Failed to save report");
      
      try {
        const localSave = {
          ...reportToSave,
          savedLocally: true,
          localTimestamp: Date.now(),
        };
        localStorage.setItem(`ai_report_${Date.now()}`, JSON.stringify(localSave));
        setReportStatus(STATUS.LOCKED);
        setStatusMsg("✅ Saved locally and locked (backend unavailable)");
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (err) {
        console.error("Local save also failed:", err);
      }
    } finally {
      setIsSaving(false);
    }
  };
  // ======================================================
  // UNLOCK REPORT (Locked → Open)
  // ======================================================
  const handleUnlockReport = async () => {
    if (reportStatus !== STATUS.LOCKED) return;
    if (!window.confirm("Unlock report for editing?")) return;
    setIsUpdatingStatus(true);
    try {
      const ok = await updateStudyStatus(STATUS.OPEN);
      if (ok) {
        setReportStatus(STATUS.OPEN);
        setStatusMsg("🔓 Report unlocked for editing.");
      } else {
        setReportStatus(STATUS.OPEN);
        setStatusMsg("🔓 Unlocked locally.");
      }
    } catch {
      setReportStatus(STATUS.OPEN);
      setStatusMsg("🔓 Unlocked locally.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // ======================================================
  // SIGN OFF REPORT (Locked → Signed Off)
  // ======================================================
  const handleSignOffReport = async () => {
    if (reportStatus !== STATUS.LOCKED) {
      setStatusMsg("⚠ Save and lock the report before signing off.");
      return;
    }
    
    if (!window.confirm("Sign off this report? This action cannot be undone.")) return;
    
    setIsUpdatingStatus(true);
    setStatusMsg("✍ Signing off...");
    
    try {
      const ok = await updateStudyStatus(STATUS.SIGNED_OFF);
      if (ok) {
        setReportStatus(STATUS.SIGNED_OFF);
        setStatusMsg("✅ Report signed off.");
      } else {
        setReportStatus(STATUS.SIGNED_OFF);
        setStatusMsg("✅ Signed off locally.");
      }
    } catch {
      setReportStatus(STATUS.SIGNED_OFF);
      setStatusMsg("✅ Signed off locally.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // ======================================================
  // DERIVED FLAGS
  // ======================================================
  const isLocked = reportStatus === STATUS.LOCKED;
  const isSignedOff = reportStatus === STATUS.SIGNED_OFF;
  const isEditable = reportStatus === STATUS.OPEN;
  const statusStyle = STATUS_COLORS[reportStatus] || STATUS_COLORS.open;

  // ======================================================
  // PRINT FUNCTION
  // ======================================================
  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      const printContent = reportRef.current?.innerHTML;
      const printWindow = window.open("", "_blank", "width=900,height=700");
      printWindow.document.write(`
        <html>
          <head>
            <title>MRI Radiology Report</title>
            <style>
              * { box-sizing: border-box; margin: 0; padding: 0; }
              body { font-family: 'Georgia', serif; background: #fff; color: #1e293b; padding: 30px; }
              .report-title { text-align: center; font-size: 28px; font-weight: 700; letter-spacing: 3px; margin-bottom: 25px; color: #1e1b4b; }
              .patient-box { background: #eef2ff; border-radius: 10px; padding: 20px; margin-bottom: 25px; border-left: 5px solid #2563eb; }
              .patient-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; font-size: 15px; }
              .section-card { background: #fff; padding: 20px; border-radius: 10px; margin-bottom: 20px; border: 1px solid #e2e8f0; page-break-inside: avoid; }
              .section-title { background: linear-gradient(90deg, #2563eb, #4f46e5); color: #fff; padding: 10px 15px; border-radius: 8px; margin-bottom: 14px; font-size: 16px; font-weight: 600; }
              .section-content { line-height: 1.9; font-size: 14px; color: #1e293b; white-space: pre-wrap; }
              .signature { margin-top: 60px; text-align: right; }
              .signature-line { width: 220px; border-top: 2px solid #000; margin-left: auto; margin-bottom: 6px; }
              .status-chip { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 13px; font-weight: 600; margin-left: 12px; vertical-align: middle; }
              .status-open { background: #e8f5e9; color: #2e7d32; border: 1px solid #a5d6a7; }
              .status-locked { background: #fff3e0; color: #e65100; border: 1px solid #ffcc80; }
              .status-signed_off { background: #e3f2fd; color: #0d47a1; border: 1px solid #90caf9; }
            </style>
          </head>
          <body>${printContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        setIsPrinting(false);
      }, 500);
    }, 100);
  };

  // ======================================================
  // PDF DOWNLOAD FUNCTION
  // ======================================================
  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const element = reportRef.current;
      if (!element) return;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f5f7fb",
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      const fileName = `MRI_Report_${patientName.replace(/\s+/g, "_")}_${patientId}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error("PDF generation error:", err);
      alert("PDF generation failed. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // ======================================================
  // SHARED BUTTON STYLE
  // ======================================================
  const btnBase = {
    border: "none",
    padding: "8px 18px",
    borderRadius: "7px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "opacity 0.2s",
  };

  // ======================================================
  // UI
  // ======================================================
  return (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 999999, display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
    <div style={{ width: "95%", maxWidth: "1200px", height: "95vh", display: "flex", flexDirection: "column", background: "#f5f7fb", borderRadius: "18px", boxShadow: "0 0 40px rgba(0,0,0,0.4)", overflow: "hidden" }}>{/* ===== HEADER ===== */}
      <div style={{ background: "linear-gradient(90deg,#0f172a,#1e293b)", color: "#fff", padding: "16px 25px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, flexWrap: "wrap", gap: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <h2 style={{ margin: 0, letterSpacing: "2px", fontSize: "20px" }}>
            🧠 AI REPORTING TOOL
          </h2>
            {/* ── Status Badge ── */}
          <span style={{ background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}`, borderRadius: "5px", padding: "2px 12px", fontSize: "12px", fontWeight: 600, letterSpacing: "0.4px", display: "inline-flex", alignItems: "center", gap: "5px" }}>
            {reportStatus === STATUS.OPEN && "🟢"}
            {reportStatus === STATUS.LOCKED && "🔒"}
            {reportStatus === STATUS.SIGNED_OFF && "✅"}
            {STATUS_LABELS[reportStatus]}
          </span>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          {/* STATUS MESSAGE */}
          {statusMsg !== "Ready" && (
            <span style={{ fontSize: "13px", color: "#94a3b8", marginRight: "8px" }}>
              {statusMsg}
            </span>
          )}
          {/* SAVE BUTTON - hidden when signed off */}
          {!isSignedOff && (
            <button
              onClick={handleSaveReport}
              disabled={isSaving || isLocked}
              style={{
                ...btnBase,
                background: isLocked ? "#94a3b8" : "#16a34a",
                color: "#fff",
                opacity: isLocked ? 0.6 : 1,
                cursor: isLocked ? "not-allowed" : "pointer",
              }}
              title={isLocked ? "Report is already saved and locked" : ""}
            >
              {isSaving ? "💾 Saving..." : "💾 Save"}
            </button>
          )}
          {/* UNLOCK BUTTON - only when locked */}
          {isLocked && (
            <button
              onClick={handleUnlockReport}
              disabled={isUpdatingStatus}
              style={{
                ...btnBase,
                background: "#fff3e0",
                color: "#e65100",
                border: "1px solid #ffcc80",
              }}
            >
              🔓 Unlock
            </button>
          )}
          {/* SIGN OFF BUTTON - only when locked */}
          {isLocked && (
            <button
              onClick={handleSignOffReport}
              disabled={isUpdatingStatus}
              style={{
                ...btnBase,
                background: "#e3f2fd",
                color: "#0d47a1",
                border: "1px solid #90caf9",
              }}
            >
              ✍ Sign Off
            </button>
          )}

          {/* EDIT/SAVE BUTTON - hidden when signed off */}
          {!isSignedOff && (
            <button
              onClick={() => setIsEditMode((prev) => !prev)}
              style={{
                ...btnBase,
                background: isEditMode ? "#16a34a" : "#2563eb",
                color: "#fff",
              }}
            >
              {isEditMode ? "💾 Save Edits" : "✏️ Edit"}
            </button>
          )}

          {/* PRINT BUTTON */}
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            style={{ ...btnBase, background: "#2563eb", color: "#fff", opacity: isPrinting ? 0.7 : 1 }}
          >
            🖨️ {isPrinting ? "Printing..." : "Print"}
          </button>

          {/* PDF BUTTON */}
          <button
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            style={{ ...btnBase, background: "#2563eb", color: "#fff", opacity: isGeneratingPDF ? 0.7 : 1 }}
          >
            📄 {isGeneratingPDF ? "Generating..." : "PDF"}
          </button>

          {/* CLOSE BUTTON */}
          <button
            onClick={onClose}
            style={{ ...btnBase, background: "#64748b", color: "#fff", padding: "8px 14px" }}
          >
            ✖
          </button>
        </div>
        </div>

        {/* EDIT MODE BANNER */}
        {isEditMode && (
          <div style={{ background: "#fef3c7", borderBottom: "2px solid #f59e0b", padding: "10px 25px", fontSize: "14px", color: "#92400e", display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            ✏️ <b>Edit Mode Active</b> — You can edit the report sections. After completing your changes, click the <b>Save Edits</b> button.
          </div>
        )}
        {/* LOCKED BANNER */}
        {isLocked && (
          <div style={{ background: "#fff8e1", borderBottom: "2px solid #ffa000", padding: "8px 25px", fontSize: "13px", color: "#5d4037", display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            🔒 Report is locked. Click <strong>Unlock</strong> to make edits, or <strong>Sign Off</strong> to finalize.
          </div>
        )}
        {/* SIGNED OFF BANNER */}
        {isSignedOff && (
          <div style={{ background: "#e3f2fd", borderBottom: "2px solid #1565c0", padding: "8px 25px", fontSize: "13px", color: "#0d47a1", display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            ✅ Report signed off by radiologist. This report is read-only.
          </div>
        )}
        {/* ===== SCROLLABLE REPORT BODY ===== */}
        <div style={{ overflowY: "auto", flex: 1, padding: "35px" }}>
          <div ref={reportRef}>
            {/* TITLE */}
            <div className="report-title" style={{ textAlign: "center", fontSize: "34px", fontWeight: "700", letterSpacing: "3px", marginBottom: "30px", color: "#1e1b4b" }}>
              MRI RADIOLOGY REPORT
              {/* Status in title for print */}
              <span className={`status-chip status-${reportStatus}`} style={{ display: "inline-block", padding: "4px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: "600", marginLeft: "12px", verticalAlign: "middle", background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>
                {STATUS_LABELS[reportStatus]}
              </span>
            </div>

            {/* PATIENT BOX */}
            <div className="patient-box" style={{ background: "#eef2ff", borderRadius: "16px", padding: "28px", marginBottom: "32px", borderLeft: "6px solid #2563eb" }}>
              <div className="patient-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", fontSize: "16px" }}>
                <div><b>Patient Name:</b> {patientName}</div>
                <div><b>Patient ID:</b> {patientId}</div>
                <div><b>Study ID:</b> {studyId}</div>
                <div><b>Study Description:</b> {studyDescription}</div>
                <div><b>Modality:</b> MRI</div>
                <div><b>Date:</b> {new Date().toLocaleString()}</div>
              </div>
            </div>

            {/* REPORT SECTIONS */}
            {sections.map((item, index) => {
              const isReadOnly = isLocked || isSignedOff;
              return (
                <div key={index} className="section-card" style={{ background: "#fff", padding: "22px", borderRadius: "14px", marginBottom: "22px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", border: isEditMode && !isReadOnly ? "2px dashed #2563eb" : "2px solid transparent", opacity: isReadOnly ? 0.85 : 1, transition: "border 0.2s" }}>
                <div className="section-title" style={{ background: "linear-gradient(90deg,#2563eb,#4f46e5)", color: "#fff", padding: "11px 16px", borderRadius: "9px", marginBottom: "16px", fontSize: "18px", fontWeight: "600" }}>
                  {item.title}
                </div>
                {isEditMode && !isReadOnly ? (
                  <textarea
                    value={item.value || ""}
                    onChange={(e) => updateSection(index, e.target.value)}
                    style={{ width: "100%", minHeight: "140px", lineHeight: "1.9", fontSize: "15px", color: "#1e293b", border: "1px solid #c7d2fe", borderRadius: "8px", padding: "12px", resize: "vertical", fontFamily: "inherit", background: "#f8faff", outline: "none", boxSizing: "border-box" }}
                  />
                  ) : (
                    <div
                      className="section-content"
                      style={{ lineHeight: "1.9", fontSize: "15px", color: isReadOnly ? "#64748b" : "#1e293b", whiteSpace: "pre-wrap" }}
                      dangerouslySetInnerHTML={{ __html: item.value || "N/A" }}
                    />
                  )}
                </div>
              );
            })}

            {/* SIGNATURE */}
            <div className="signature" style={{ marginTop: "80px", textAlign: "right" }}>
              <div
                className="signature-line"
                style={{ width: "240px", borderTop: "2px solid #000", marginLeft: "auto", marginBottom: "8px" }}
              />
              <div style={{ fontSize: "15px", color: "#374151" }}>
                Radiologist Signature
                {isSignedOff && (
                  <div style={{ color: "#0d47a1", fontWeight: 600, marginTop: "4px" }}>
                    ✅ Digitally Signed Off — {new Date().toLocaleString()}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// OPEN LOADER HELPER
// ======================================================
const openLoader = () => {
  const div = document.createElement("div");
  div.id = "ai-report-loader-root";
  document.body.appendChild(div);
  const root = ReactDOM.createRoot(div);
  root.render(<ReportGeneratingLoader />);
  return () => {
    root.unmount();
    if (document.body.contains(div)) {
      document.body.removeChild(div);
    }
  };
};

// ======================================================
// OPEN POPUP HELPER
// ======================================================
const openAIReportPopup = (reportData, patient) => {
  const div = document.createElement("div");
  document.body.appendChild(div);
  const root = ReactDOM.createRoot(div);
  const closePopup = () => {
    root.unmount();
    document.body.removeChild(div);
  };
  root.render(
    <AIReportPopup report={reportData} patient={patient} onClose={closePopup} />
  );
};

// ======================================================
// TOOL CONFIG
// ======================================================
const AIReportToolConfig = {
  name: "AI Report",
  tool: "AIReportTool",

  customAction: async (studyId, setIsGenerating, setSelectedStudy, patient) => {
    // ── Study ID Extraction ──
    let extractedStudyId = studyId;
    if (!extractedStudyId && patient) {
      if (typeof patient === "object") {
        extractedStudyId =
          patient?.study_id ||
          patient?.studyId ||
          patient?.StudyInstanceUID ||
          patient?.study?.study_id ||
          patient?.study?.studyId ||
          patient?.study?.StudyInstanceUID ||
          patient?.mainStudyId ||
          null;
      }
      if (!extractedStudyId && typeof patient === "string") {
        extractedStudyId = patient;
      }
    }
    if (!extractedStudyId) {
      alert("Study ID not found");
      return null;
    }

    // ── Show Loader ──
    setIsGenerating?.(true);
    const closeLoader = openLoader();
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/studies/${extractedStudyId}/generate-report/`
      );
      setSelectedStudy?.(extractedStudyId);
      closeLoader();
      openAIReportPopup(response.data, patient);
      return response.data;
    } catch (err) {
      console.error(err);
      closeLoader();
      alert(err?.response?.data?.error || err.message || "Report generation failed");
      return null;
    } finally {
      setIsGenerating?.(false);
    }
  },

  // ── Icon ──
  icon: (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="7" width="14" height="10" rx="3" ry="3" />
      <circle cx="10" cy="12" r="1" fill="currentColor" />
      <circle cx="14" cy="12" r="1" fill="currentColor" />
      <path d="M9 15c1 .8 5 .8 6 0" />
      <path d="M12 3v3" />
      <circle cx="12" cy="2" r="1" fill="currentColor" />
      <path d="M5 11H3" />
      <path d="M21 11h-2" />
      <path d="M9 20h6" />
    </svg>
  ),
};

export default AIReportToolConfig;